import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { onScrollFrame } from '@/lib/scroll'
import { Wash } from '@/components/generals/backdrops'
import { TornCorner } from '@/components/generals/paper'
import { MockScale } from '@/components/generals/mock-scale'

export interface PinnedStep {
    /** The short name the pinned rail shows while this step is on screen, for example "Shortlist". */
    label: string
    title: string
    body: string
    window: ReactNode
}

interface PinnedStepsProps {
    id: string
    title: string
    /** What the rail reads before the first step reaches the middle of the screen. */
    kicker: string
    steps: PinnedStep[]
    /** Which side the rail is pinned to. Panels bleed off the opposite edge of the screen. */
    rail: 'left' | 'right'
    /** Keep the heading for screen readers only. */
    hideTitle?: boolean
    /** The width every step's mock is drawn at, in px. */
    mockWidth: number
    /** The width of the mocks' narrow layout on phones, in px. */
    mockNarrowWidth?: number
    /** Hold step copy to a narrower measure. */
    narrowBody?: boolean
    className?: string
}

/**
 * A pinned margin column beside large translucent panels. The margin carries a kicker that
 * swaps to the current step's name, with a small step indicator under it. Each panel holds a
 * torn corner, the step's numeral, title and copy, and its product mock, which overruns the
 * panel's cut edge slightly. Below 1100px the margin goes; below 992px copy stacks over its mock.
 */
export const PinnedSteps: FC<PinnedStepsProps> = ({ id, title, kicker, steps, rail, hideTitle = false, mockWidth, mockNarrowWidth, narrowBody = false, className }): ReactNode => {
    const [active, setActive] = useState(-1)
    const panelRefs = useRef<Array<HTMLElement | null>>([])

    useEffect(() => {
        return onScrollFrame(() => {
            const middle = window.innerHeight * 0.5
            let current = -1

            panelRefs.current.forEach((panel, index) => {
                if (panel && panel.getBoundingClientRect().top < middle) current = index
            })

            setActive(previous => (previous === current ? previous : current))
        })
    }, [steps.length])

    const railLeft = rail === 'left'
    const titleId = `${id}-title`

    const margin = (
        <aside
            aria-hidden='true'
            className={cn('sticky top-[92px] hidden h-[calc(100svh-92px-56px)] flex-col justify-between self-start min-[1101px]:flex', !railLeft && 'min-[1101px]:col-start-2 min-[1101px]:row-start-1')}
        >
            <p className='relative h-5 whitespace-nowrap text-[14px] text-ink/80'>
                {[kicker, ...steps.map(step => step.label)].map((text, index) => (
                    <span
                        key={text}
                        className={cn(
                            'absolute left-0 top-0 transition-[opacity,translate] duration-150 ease-out motion-reduce:transition-none',
                            index === active + 1 ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
                        )}
                    >
                        {text}
                    </span>
                ))}
            </p>

            <ol className='flex flex-col gap-2'>
                {steps.map((step, index) => (
                    <li key={step.label} className='flex items-center gap-2.5'>
                        <span className={cn('block w-px rounded-full transition-[height,background-color] duration-500 ease-soft', index === active ? 'h-7 bg-ink/80' : 'h-3.5 bg-ink/20')} />
                        <span className={cn('font-mono text-[11px] transition-colors duration-500', index === active ? 'text-ink/80' : 'text-ink/35')}>
                            {String(index + 1).padStart(2, '0')}
                        </span>
                    </li>
                ))}
            </ol>
        </aside>
    )

    return (
        <section
            id={id}
            aria-labelledby={titleId}
            className={cn(
                'relative isolate mx-auto grid max-w-[calc(1080px+2*clamp(16px,3vw,32px))] grid-cols-1 pb-[clamp(48px,5vw,72px)] pt-[clamp(48px,5vw,80px)] min-[1101px]:gap-[clamp(20px,2.5vw,36px)]',
                railLeft
                    ? 'pl-[clamp(16px,3vw,32px)] min-[1101px]:grid-cols-[clamp(150px,16vw,240px)_1fr]'
                    : 'pr-[clamp(16px,3vw,32px)] min-[1101px]:grid-cols-[1fr_clamp(150px,16vw,240px)]',
                className
            )}
        >
            <Wash strength='medium' />

            {margin}

            <div className={cn('min-w-0', !railLeft && 'min-[1101px]:col-start-1 min-[1101px]:row-start-1')}>
                <h2 id={titleId} className={cn(hideTitle ? 'sr-only' : 'type-section reveal', !hideTitle && !railLeft && 'pl-[clamp(16px,3vw,32px)]')}>
                    {title}
                </h2>

                <ol aria-label={title} className={cn('flex flex-col gap-[clamp(40px,4.5vw,64px)]', !hideTitle && 'mt-[clamp(48px,5.5vw,88px)]')}>
                    {steps.map((step, index) => (
                        <li key={step.label}>
                            <article
                                id={`${id}-step-${index + 1}`}
                                ref={element => {
                                    panelRefs.current[index] = element
                                }}
                                className={cn(
                                    'reveal relative grid scroll-mt-[90px] grid-cols-1 items-end gap-7 overflow-hidden bg-[rgb(250_249_246/0.4)] pb-[clamp(48px,4.5vw,68px)] pt-[clamp(72px,18vw,120px)] [--bleed:max(0px,50vw_-_572px)] min-[992px]:min-h-[clamp(480px,47vw,680px)] min-[992px]:gap-[clamp(32px,4.5vw,70px)] min-[992px]:pt-[clamp(120px,13vw,190px)]',
                                    railLeft
                                        ? 'mr-[calc(-1*var(--bleed))] pl-[clamp(24px,2.6vw,40px)] min-[992px]:grid-cols-[minmax(200px,280px)_1fr]'
                                        : 'ml-[calc(-1*var(--bleed))] pr-[clamp(24px,2.6vw,40px)] min-[992px]:grid-cols-[1fr_minmax(200px,280px)]'
                                )}
                            >
                                <TornCorner className={cn(!railLeft && 'left-auto right-0 -scale-x-100')} />

                                <div className={cn('relative', railLeft ? 'max-[991px]:pr-6' : 'max-[991px]:pl-[clamp(16px,3vw,32px)] min-[992px]:col-start-2 min-[992px]:row-start-1')}>
                                    <p aria-hidden='true' className='numeral mb-6 text-[clamp(76px,7.5vw,104px)] min-[992px]:mb-[clamp(36px,4vw,56px)]'>
                                        {String(index + 1).padStart(2, '0')}
                                    </p>
                                    <h3 className='type-step'>
                                        <span className='sr-only'>Step {index + 1}: </span>
                                        {step.title}
                                    </h3>
                                    <p className={cn('mt-2.5 text-ink/[0.88]', narrowBody ? 'max-w-[34ch]' : 'max-[991px]:max-w-[44ch]')}>
                                        {step.body}
                                    </p>
                                </div>

                                <div className={cn('relative min-w-0', railLeft ? 'max-[991px]:pr-6' : 'max-[991px]:pl-[clamp(16px,3vw,32px)] min-[992px]:col-start-1 min-[992px]:row-start-1')}>
                                    <div className={cn('min-[992px]:w-[calc(100%+24px)]', !railLeft && 'min-[992px]:-ml-6')}>
                                        <MockScale designWidth={mockWidth} narrowWidth={mockNarrowWidth}>
                                            {step.window}
                                        </MockScale>
                                    </div>
                                </div>
                            </article>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}
